import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import type { DonutSlice } from './donut-chart';

// A bucket that rounds to zero still exists, so it must not read as "none".
function percentText(slice: DonutSlice): string {
  return slice.percent === 0 ? '<1%' : `${slice.percent}%`;
}

type Props = {
  slices: DonutSlice[];
  activeId: string | null;
  onHover: (id: string | null) => void;
};

// Rows are not buttons — there is nothing to activate, and a fake control
// would promise an action that does not exist.
type RowProps = {
  slice: DonutSlice;
  dimmed: boolean;
  onHover: (id: string | null) => void;
};

function LegendRow({ slice, dimmed, onHover }: RowProps): ReactNode {
  return (
    <li
      className={cn(
        'flex items-center gap-sm rounded px-1 py-[3px] transition-opacity duration-200',
        dimmed && 'opacity-40'
      )}
      onMouseEnter={() => onHover(slice.id)}
      onMouseLeave={() => onHover(null)}
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: slice.color }}
      />
      <LegendText slice={slice} />
    </li>
  );
}

// Text wears text tokens, never the slice color: a light hue is illegible
// as type. The swatch beside it is what carries the identity.
function LegendText({ slice }: { slice: DonutSlice }): ReactNode {
  return (
    <>
      <span className="min-w-0 flex-1 truncate text-xs text-text-secondary">
        {slice.label}
      </span>
      <span className="font-mono text-xs tabular-nums text-text-primary">
        {percentText(slice)}
      </span>
      <span className="w-8 shrink-0 text-right font-mono text-[11px] tabular-nums text-text-tertiary/70">
        {slice.value}
      </span>
    </>
  );
}

// The legend is the accessible readout, not a decoration: every slice
// shows its label, its share and its raw count, so nothing is reachable
// only by hovering the ring.
export function DonutLegend({ slices, activeId, onHover }: Props): ReactNode {
  // Width is capped so the counts stay next to their labels instead of
  // drifting to the far edge of a wide column.
  return (
    <ul className="min-w-0 max-w-[15rem] flex-1 space-y-px">
      {slices.map((slice) => (
        <LegendRow
          key={slice.id}
          slice={slice}
          dimmed={activeId !== null && activeId !== slice.id}
          onHover={onHover}
        />
      ))}
    </ul>
  );
}
